import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '@material-ui/core';
import AssignmentReturned from '@material-ui/icons/AssignmentReturned';
import makeStyles from '@material-ui/styles/makeStyles';
import { get, post, put } from 'helpers/apiHelpers';
import AdminTable from 'layouts/AdminTable';
import buttonsStyle from 'assets/jss/material-dashboard-pro-react/views/buttonsStyle';
import DataGrid from 'components/DataGrid/DataGrid';
import DataGridStateContextProvider from 'components/DataGrid/DataGridStateContext';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import { toast } from 'react-toastify';

import useGetColumns from './useGetColumns';
import AssignDriverModal from './AssignDriverModal';
import AssignZonesSection from './AssignZonesSection';
import AssignZonesWithClearAllSection from './AssignZonesWithClearAllSection';

const useStyles = makeStyles(buttonsStyle);

const List = () => {
  const { t } = useTranslation();
  const classes = useStyles();

  const columns = useGetColumns();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [zone, setZone] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [gridProps, setGridProps] = useState(null);

  // eslint-disable-next-line no-shadow -- FIXME
  const fetchZone = zone => {
    setIsLoading(true);
    // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
    get(zone).then(
      res => {
        setZone(res);
        setIsLoading(false);
      },
      () => {
        setIsLoading(false);
        toast.error(t('addresses.cannotDownloadZone'));
      }
    );
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedAddress(null);
    setZone(null);
    setSelectedDriver(null);
  };

  const openModal = (row, props) => {
    if (row.zone) {
      fetchZone(row.zone['@id']);
    }

    setGridProps(props);
    setIsModalOpen(true);
    setSelectedAddress(row);
    setSelectedDriver(row.driver);
  };

  const handleDriverSelect = (e, driver) => {
    setSelectedDriver(driver);
  };

  const handleDriverChange = () => {
    setIsLoading(true);
    // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
    put(selectedAddress['@id'], { driver: selectedDriver['@id'] }).then(
      () => {
        gridProps.grid.current.fireFetchData();
        closeModal();
        setIsLoading(false);
        toast.success(t('addresses.changedDriver'));
      },
      () => {
        setIsLoading(false);
        toast.error(t('addresses.cantChangeDriver'));
      }
    );
  };

  const handleAssignZones = () => {
    setIsLoading(true);
    // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
    post('/actions/assign-zone-to-addresses').then(
      () => {
        setIsLoading(false);
        toast.success(`${t('addresses.mapZones')} ${t('addresses.mapZones2')}`);
      },
      () => {
        setIsLoading(false);
        toast.error(t('addresses.cantMap'));
      }
    );
  };

  const handleAssignZonesWithClearAll = () => {
    setIsLoading(true);
    // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
    post('/actions/assign-zone-to-addresses?clearAll=true').then(
      () => {
        setIsLoading(false);
        toast.success(`${t('addresses.mapZones')} ${t('addresses.mapZones2')}`);
      },
      () => {
        setIsLoading(false);
        toast.error(t('addresses.cantMap'));
      }
    );
  };

  const manipulateQuery = (requestData, query) => query;

  const customActions = row => [
    {
      action: props => {
        openModal(row, props);
      },
      icon: <AssignmentReturned />,
      disabled: false,
      color: 'primary',
      buttonText: t('addresses.changeDriver'),
      simple: true,
      style: { width: 'auto' },
    },
  ];

  return (
    <DataGridStateContextProvider>
      <AdminTable title={t('addresses.list')}>
        <AssignDriverModal
          classes={classes}
          isLoading={isLoading}
          modalOpen={isModalOpen}
          closeModal={closeModal}
          selectedAddress={selectedAddress}
          selectedDriver={selectedDriver}
          handleDriverSelect={handleDriverSelect}
          handleDriverChange={handleDriverChange}
          zone={zone}
        />
        <GridContainer>
          <GridItem>
            <AssignZonesSection
              disabled={isLoading}
              handleAssignZones={handleAssignZones}
            />
          </GridItem>
          <GridItem>
            <AssignZonesWithClearAllSection
              disabled={isLoading}
              handleAssignZones={handleAssignZonesWithClearAll}
            />
          </GridItem>
        </GridContainer>
        <Divider />
        <DataGrid
          refreshOnFetch={true}
          actions={true}
          export={true}
          paginationTop={true}
          paginationBottom={false}
          customActions={customActions}
          customActionsWidth={200}
          url="/addresses"
          reportName="address"
          role="ADDRESSES"
          columns={columns}
          manipulateQuery={manipulateQuery}
          customActionsColumnDirection="actions-left"
        />
      </AdminTable>
    </DataGridStateContextProvider>
  );
};

export default List;
